const STORAGE_KEY = "vf.cortexSelection.v1";

export interface CortexSelection {
  text: string;
  language: string;
  filePath: string;
  pageUrl: string;
  updatedAt: number;
}

interface CortexSelectionRecord {
  selection: CortexSelection | null;
}

let hasLoaded = false;
let cached: CortexSelection | null = null;
const listeners = new Set<() => void>();

function emitChange(): void {
  for (const listener of listeners) listener();
}

function getCurrentPageUrl(): string {
  if (typeof window === "undefined") return "";
  return (
    window.location.pathname + window.location.search + window.location.hash
  );
}

function loadFromStorage(): void {
  if (hasLoaded) return;
  hasLoaded = true;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as CortexSelectionRecord;
    cached = parsed?.selection ?? null;
  } catch {
    cached = null;
  }
}

function saveToStorage(selection: CortexSelection | null): void {
  try {
    const record: CortexSelectionRecord = { selection };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {
    // ignore storage failures (private mode, quota, etc.)
  }
}

export function getCortexSelection(): CortexSelection | null {
  loadFromStorage();
  return cached;
}

export function subscribeCortexSelection(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getCortexSelectionForCurrentPage(): CortexSelection | null {
  const selection = getCortexSelection();
  if (!selection) return null;
  return selection.pageUrl === getCurrentPageUrl() ? selection : null;
}

export function clearCortexSelection(): void {
  cached = null;
  hasLoaded = true;
  saveToStorage(null);
  emitChange();
}

export function setCortexSelection(input: {
  text: string;
  language?: string;
  filePath?: string;
  pageUrl?: string;
  updatedAt?: number;
}): void {
  const text = input.text?.trim() ?? "";
  if (!text) {
    clearCortexSelection();
    return;
  }

  const next: CortexSelection = {
    text,
    language: input.language ?? "",
    filePath: input.filePath ?? "",
    pageUrl: input.pageUrl ?? getCurrentPageUrl(),
    updatedAt: input.updatedAt ?? Date.now(),
  };

  cached = next;
  hasLoaded = true;
  saveToStorage(next);
  emitChange();
}
